import React, { useState } from 'react';
import { Map as LeafletMap } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-markercluster';
import { useAppConfiguration } from '../../AppConfiguration';
import { MapboxTileLayer } from '../../api/mapbox';
import './loadThirdPartyPlugins';
import GestureHandler from './GestureHandler';
import * as Klaro from 'klaro';
import getKlaroConfig from '../../config/klaro';
import MapBoxConsentDialog from './MapBoxConsentDialog';

export const CachedMarkerClusterGroup = React.memo(({markers, ...rest}) => (
    <MarkerClusterGroup removeOutsideVisibleBounds={false} chunkedLoading={true} {...rest}>
        {markers}
    </MarkerClusterGroup>
));

const MapMultiple = ({
    center = [51.052888, 13.738229],
    zoom = 13,
    items,
    info,
    loading,
    boundingBox,
    setBoundingBox,
    children,
}) => {
    // Get screen height. We can use window.innerHeight to get the perfect size if we are not in an iframe / widget.
    const { isWidget } = useAppConfiguration();
    const screenHeight = isWidget ? window.screen.availHeight : window.innerHeight;

    const consentManager = Klaro.getManager(getKlaroConfig());
    const [mapBoxConsent, setMapBoxConsent] = useState(consentManager.getConsent('mapbox'));

    consentManager.watch({
        update: (obj, name) => {
            if (name === 'applyConsents') {
                setMapBoxConsent(obj.savedConsents.mapbox);
            }
        }
    });

    const bounds = [];

    function updateBoundingBox({target: map}) {
        setBoundingBox(map.getBounds().toBBoxString());
    }

    if (!boundingBox) {
        for (const item of items) {
            bounds.push([item.lat, item.lng]);
        }
    } else {
        const [southwestLng, southwestLat, northeastLng, northeastLat] = boundingBox.split(',');

        bounds.push([southwestLat, southwestLng]);
        bounds.push([northeastLat, northeastLng]);
    }

    return (
        <div className="map-container" style={{
            '--screen-height': screenHeight + 'px',
        }}>
            <div className="custom-content">
                <div className="info-box">{info}</div>
            </div>
            <LeafletMap
                center={center}
                zoom={zoom}
                bounds={bounds.length > 0 ? bounds : null}
                gestureHandling={false}
                gestureHandlingOptions={{
                    text: {
                        touch: 'Verschieben der Karte mit zwei Fingern',
                        scroll: 'Verwende STRG + Scrollen zum Zoomen der Karte',
                        scrollMac: 'Verwende \u2318 + Scrollen zum Zoomen der Karte',
                    },
                    duration: 1000,
                }}
                whenReady={updateBoundingBox}
                onMoveEnd={updateBoundingBox}
            >
                {mapBoxConsent ?
                    <>
                        <GestureHandler />

                        <div className="leaflet-top leaflet-left leaflet-control-loading-container">
                            {loading && <div className="leaflet-bar leaflet-control">
                                <div className="leaflet-control-loading">
                                    <i className="la la-spinner" />
                                </div>
                            </div>}
                        </div>

                        <MapboxTileLayer />

                        {/* Use cluster with a max radius of 0 so locations that have the same coordinates can be accessed via
                    the spider legs feature */}
                        {items && items.length > 0 && (
                            <CachedMarkerClusterGroup markers={items.map(({marker}) => marker)} maxClusterRadius={0} />
                        )}

                        {/* Insert extra layers */}
                        {children}
                    </>
                    :
                    <MapBoxConsentDialog />
                }
            </LeafletMap>
        </div>
    );
};

export default React.memo(MapMultiple);
